import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { ArrowRight, CalendarIcon, Lock, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router'

import rescueXIcon from '@/assets/images/icon.png'
import CertificateCard from '@/components/CertificateCard/CertificateCard'
import { ConfirmButton } from '@/components/ConfirmButton'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Skeleton } from '@/components/elements/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/elements/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/elements/tooltip'
import { Layout } from '@/components/Layout'
import StatusIcon from '@/components/StatusIcon/StatusIcon'
import { TextLink } from '@/components/TextLink'
import EventSlideoutView from '@/pages/Accounts/Calendar/EventSlideoutView'
import {
  getStudentEntityCertificates,
  removeStudentCertificate,
} from '@/services/entity_student_certificates.service'
import { UserStoreContext } from '@/stores/UserStore'
import { addCanStartToCourseRecords, getNextActionableCourseRecord } from '@/utils/certificates'
import { formatDate, formatTimeRange } from '@/utils/formatters'
import { toast } from '@/utils/helpers'

const StudentCourses = observer(() => {
  // Context
  const { memberRoleForActiveEntity } = useContext(UserStoreContext)
  const studentId = memberRoleForActiveEntity?.id
  const { entityId } = useParams()

  // State
  const [viewEvent, setViewEvent] = useState(null)

  // Data fetching
  const certificateQuery = useQuery({
    queryKey: ['accounts', 'students', 'certificates', entityId, studentId],
    queryFn: () => getStudentEntityCertificates({ entityId, studentId }),
    enabled: !!studentId,
  })

  // Students can remove their own not-yet-started courses (the backend allows
  // self-removal only while status is Not Started, same as the manager rule).
  const queryClient = useQueryClient()
  const { mutate: removeCourse } = useMutation({
    mutationFn: (studentCertificateId) => removeStudentCertificate(entityId, studentCertificateId),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Course removed', 'success')
      queryClient.invalidateQueries({
        queryKey: ['accounts', 'students', 'certificates', entityId, studentId],
      })
    },
  })

  const allCertificates = useMemo(() => {
    if (!certificateQuery.data) return []
    return certificateQuery.data.results.map(addCanStartToCourseRecords)
  }, [certificateQuery.data])
  if (!studentId) {
    return (
      <Layout breadcrumbs={['Courses']}>
        <Layout.Heading title="Courses" />
        <p className="text-sm text-gray-500">You do not have access to any student courses.</p>
      </Layout>
    )
  }
  if (certificateQuery.isPending) {
    return (
      <Layout breadcrumbs={['Courses']}>
        <Layout.Heading title="Courses" />
        <Skeleton className="mb-4 h-8 w-1/4" />
      </Layout>
    )
  }

  const openEvent = (event) => {
    setViewEvent(event)
  }

  const renderFooter = (studentCertificate, nextActionable) => {
    const hasHandsOn = studentCertificate.trainingStatus !== null

    const resumeButton = nextActionable && (
      <Button asChild>
        <Link to={`/accounts/${entityId}/students/courses/${nextActionable.id}`}>
          {nextActionable.status === 'In Progress' ? 'Resume' : 'Start'}:{' '}
          {nextActionable.course.name}
          <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
        </Link>
      </Button>
    )

    if (!hasHandsOn) {
      return resumeButton ? <div className="flex flex-wrap gap-2">{resumeButton}</div> : null
    }

    if (studentCertificate.event)
      return (
        <section className="flex w-full flex-col gap-4">
          <h3 className="text-xl">Registered: {studentCertificate.event.name}</h3>
          <div className="text-muted-foreground flex gap-4 text-sm">
            <CalendarIcon className="h-6 w-6" />
            <div className="flex flex-col gap-1">
              <p>
                <strong>Date: </strong> {formatDate(studentCertificate.event.date)}
              </p>
              <p>
                <strong>Time: </strong>{' '}
                {formatTimeRange(
                  studentCertificate.event.startTime,
                  studentCertificate.event.endTime,
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => openEvent(studentCertificate.event)}>
              Event Details
            </Button>
            {resumeButton}
          </div>
        </section>
      )
    return (
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline">
          <Link
            to={`/accounts/${entityId}/calendar`}
            state={{ certificate: studentCertificate.certificate.id }}
          >
            Schedule Training
          </Link>
        </Button>
        {resumeButton}
      </div>
    )
  }

  // These are the current user's own courses (the page is scoped to them), so
  // each one gets a destructive "remove" strip pinned to its bottom edge.
  // Removal is limited to custom (facility-authored) courses and works at any
  // status — a temporary workaround until course previews/versioning exist so a
  // student can drop and retake a course. Standard courses can't be self-removed.
  const removeBanner = (studentCertificate) => {
    if (studentCertificate.certificate.certificateType !== 'Custom Student Course') return null
    return (
      <ConfirmButton
        type="button"
        variant="plain"
        size="plain"
        className="border-destructive/15 text-destructive hover:bg-destructive/10 bg-destructive/5 flex w-full items-center justify-center gap-2 border-t px-4 py-3 text-sm font-medium transition-colors"
        onClick={() => removeCourse(studentCertificate.id)}
        confirmTitle="Remove course"
        confirmContent={`Remove "${studentCertificate.certificate.name}" from your courses? Any progress and certificate for it will be removed.`}
      >
        <Trash2 className="h-4 w-4" />
        Remove course
      </ConfirmButton>
    )
  }
  // RescueX (standard/global) courses vs. facility-authored custom courses.
  // "Custom Student Course" is the only facility certificate type; everything
  // else (Standard, Rescue Ready, Surface Rescue, instructor courses) is a
  // RescueX course. RescueX courses lead, facility courses follow.
  const isFacilityCert = (cert) => cert.certificate.certificateType === 'Custom Student Course'
  const rescuexCertificates = allCertificates.filter((cert) => !isFacilityCert(cert))
  const facilityCertificates = allCertificates.filter(isFacilityCert)

  const renderCourseSections = (certificates) => {
    const { 'In Progress': inProgress = [], Issued: issued = [] } = _.groupBy(
      certificates,
      (cert) => (cert.status === 'Issued' ? 'Issued' : 'In Progress'),
    )
    return (
      <div className="flex flex-col gap-8">
        <section>
          <h2 className="uppercase">Assigned</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {inProgress.map((studentCertificate) => {
              const nextActionable = getNextActionableCourseRecord(studentCertificate)
              return (
                <CertificateCard
                  key={studentCertificate.id}
                  studentCertificate={studentCertificate}
                  renderOnline={(course) =>
                    course.canStart ? (
                      <div className="flex items-center gap-2">
                        <StatusIcon status={course.status} />
                        <TextLink to={course.id}>
                          {(() => {
                            if (course.status === 'Completed') return 'Review'
                            if (course.status === 'In Progress') return 'Resume'
                            return 'Start'
                          })()}
                        </TextLink>
                      </div>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger>
                          <div className="flex items-center gap-2">
                            <Lock />
                            Locked
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Content must be completed in order.</p>
                        </TooltipContent>
                      </Tooltip>
                    )
                  }
                  renderHandsOn={(cert) => {
                    if (cert.trainingStatus === null) {
                      return (
                        <div className="flex items-center gap-2">
                          <StatusIcon status="Not Started" />
                          N/A
                        </div>
                      )
                    }
                    if (cert.event && cert.trainingStatus === 'Not Started') {
                      return (
                        <div className="flex items-center gap-2">
                          <StatusIcon status="In Progress" icon="complete" />
                          Scheduled
                        </div>
                      )
                    }

                    return (
                      <div className="flex items-center gap-2">
                        <StatusIcon status={cert.trainingStatus} />
                        {cert.trainingStatus}
                      </div>
                    )
                  }}
                  footer={renderFooter(studentCertificate, nextActionable)}
                  bottomBanner={removeBanner(studentCertificate)}
                />
              )
            })}
          </div>

          {inProgress.length === 0 && (
            <p className="text-sm text-gray-500">You have no courses assigned.</p>
          )}
        </section>
        <hr />
        <section>
          <h2 className="uppercase">Completed</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {issued.map((studentCertificate) => (
              <CertificateCard
                key={studentCertificate.id}
                studentCertificate={studentCertificate}
                renderOnline={(course) => (
                  <div className="flex items-center gap-2">
                    <StatusIcon status={course.status} />
                    <TextLink to={course.id}>View</TextLink>
                  </div>
                )}
                renderHandsOn={(cert) =>
                  cert.trainingStatus === null ? (
                    <div className="flex items-center gap-2">
                      <StatusIcon status="Not Started" />
                      N/A
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <StatusIcon status={cert.trainingStatus} />
                      {cert.trainingStatus}
                    </div>
                  )
                }
                bottomBanner={removeBanner(studentCertificate)}
              />
            ))}
          </div>

          {issued.length === 0 && (
            <p className="text-sm text-gray-500">You have not completed any courses.</p>
          )}
        </section>
      </div>
    )
  }

  return (
    <Layout breadcrumbs={['Courses']}>
      <Layout.Heading title="Courses" />
      <Tabs defaultValue="rescuex" className="mt-4">
        <TabsList>
          <TabsTrigger value="rescuex">
            <img src={rescueXIcon} alt="" className="mr-1.5 h-4 w-4 shrink-0" />
            RescueX Courses
            <Badge variant="default" className="ml-2">
              {rescuexCertificates.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="facility">
            Facility Courses
            <Badge variant="default" className="ml-2">
              {facilityCertificates.length}
            </Badge>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="rescuex" className="mt-6">
          {renderCourseSections(rescuexCertificates)}
        </TabsContent>
        <TabsContent value="facility" className="mt-6">
          {renderCourseSections(facilityCertificates)}
        </TabsContent>
      </Tabs>
      {viewEvent && (
        <EventSlideoutView viewEvent={viewEvent} resetAndClose={() => setViewEvent(null)} />
      )}
    </Layout>
  )
})

export default StudentCourses
